import { isFirefox } from '@anm/helpers/is/browserCheckers';
import noop from '@anm/helpers/noop';
import { useCallback, useRef, useState, DragEvent, PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';

import payloadObserver from '../payloadObserver';

import { DragItemWrapper, PreviewContainer } from './Wrapper';
import { DragItemProps } from './types';

export * from './Wrapper';

const DRAG_ICON_DEFAULT_SIZES = {
  width: 21,
  height: 27
};

const DRAG_IMAGE_DEFAULT_COORDINATES = { x: -15, y: -10 };

const DragItem = <T extends {}>({
  id,
  sizes,
  payload,
  children,
  onDragEnd = noop,
  onDragStart = noop,
  renderPreview,
  draggable = true,
  dragPreview,
  dragIconSizes = DRAG_ICON_DEFAULT_SIZES,
  dragImageCoordinates = DRAG_IMAGE_DEFAULT_COORDINATES
}: PropsWithChildren<DragItemProps<T>>) => {
  const [isMouseEnter, setMouseEnter] = useState(false);
  const [isDragging, setDragging] = useState(false);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const handleDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      isFirefox() && e.dataTransfer.setData('text/plain', 'node'); // need for drag works in FF
      if (previewContainerRef.current) {
        const { x, y } = dragImageCoordinates;

        !sizes
          ? e.dataTransfer.setDragImage(previewContainerRef.current, x, y)
          : e.dataTransfer.setDragImage(
              previewContainerRef.current,
              sizes.width - dragIconSizes.width / 2,
              dragIconSizes.height / 2
            );
      }

      payloadObserver.next(payload);
      setDragging(true);
      onDragStart(e.target as HTMLDivElement);
    },
    [payload, payloadObserver, previewContainerRef.current, sizes, isFirefox()]
  );

  const handleDragEnd = useCallback(() => {
    payloadObserver.next(null);
    setDragging(false);
    onDragEnd();
  }, [payloadObserver]);

  return (
    <>
      {isMouseEnter &&
        createPortal(
          <PreviewContainer ref={previewContainerRef} {...{ sizes }}>
            {renderPreview ? renderPreview() : <img src={dragPreview} />}
          </PreviewContainer>,
          document.body
        )}
      <DragItemWrapper
        draggable={draggable}
        isDragging={isDragging}
        onMouseEnter={() => setMouseEnter(true)}
        onMouseLeave={() => setMouseEnter(false)}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        {...(id && { id: id as string })}
      >
        {children}
      </DragItemWrapper>
    </>
  );
};

export default DragItem;
